import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const TransportAgentsTab = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const { data: agentsData, error } = await supabase?.from('transport_agents')?.select(`
          *,
          trucks:trucks(count)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      const formatted = agentsData?.map(agent => ({
        ...agent,
        truckCount: agent?.trucks?.[0]?.count || 0,
      }));

      setAgents(formatted || []);
    } catch (err) {
      console.error('Fetch agents error:', err);
      showNotification('Failed to load transport agents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  const handleViewTrucks = async (agentId) => {
    try {
      const { data: trucks } = await supabase?.from('trucks')?.select('*')?.eq('agent_id', agentId);

      setSelectedAgent({ id: agentId, trucks: trucks || [] });
    } catch (err) {
      console.error('Fetch agent trucks error:', err);
    }
  };

  const filtered = agents?.filter(agent =>
    !search ||
    agent?.agent_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    agent?.mobile?.includes(search) ||
    agent?.city?.toLowerCase()?.includes(search?.toLowerCase())
  );

  return (
    <div>
      {notification?.show && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className={`rounded-lg p-4 border shadow-lg flex items-center gap-3 ${
            notification?.type === 'success' ?'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
          }`}>
            <Icon 
              name={notification?.type === 'success' ? 'CheckCircle' : 'XCircle'} 
              size={18} 
              className={notification?.type === 'success' ? 'text-green-600' : 'text-red-600'} 
            />
            <p className={`text-sm font-medium ${
              notification?.type === 'success' ? 'text-green-900' : 'text-red-900'
            }`}>
              {notification?.message}
            </p>
            <button 
              onClick={() => setNotification({ show: false, message: '', type: 'success' })} 
              className="ml-auto text-gray-400 hover:text-gray-600"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {selectedAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0" style={{ backgroundColor: '#ffffff' }}>
              <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>
                Trucks ({selectedAgent?.trucks?.length || 0})
              </h2>
              <button onClick={() => setSelectedAgent(null)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100">
                <Icon name="X" size={18} color="#6b7280" />
              </button>
            </div>
            <div className="p-6">
              {selectedAgent?.trucks?.length === 0 ? (
                <div className="text-center py-10">
                  <Icon name="Truck" size={40} color="#9ca3af" className="mx-auto mb-3" />
                  <p className="text-sm" style={{ color: '#6b7280' }}>No trucks registered yet for this agent</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedAgent?.trucks?.map(truck => (
                    <div key={truck?.id} className="rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-bold tracking-wide text-base" style={{ color: '#1f2937' }}>
                            {truck?.vehicle_number}
                          </span>
                          {truck?.transporter_name && (
                            <div className="text-xs mt-0.5 font-semibold" style={{ color: '#d97706' }}>
                              🚛 {truck?.transporter_name}
                            </div>
                          )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          truck?.status === 'Available' ?'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                        }`}>
                          {truck?.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span style={{ color: '#6b7280' }}>Capacity: </span>
                          <span className="font-semibold" style={{ color: '#1f2937' }}>{truck?.capacity_mt} MT</span>
                        </div>
                        <div>
                          <span style={{ color: '#6b7280' }}>Rate: </span>
                          <span className="font-semibold" style={{ color: '#1f2937' }}>₹{truck?.rate_per_ton}/T</span>
                        </div>
                        <div className="col-span-2">
                          <span style={{ color: '#6b7280' }}>Location: </span>
                          <span className="font-semibold" style={{ color: '#1f2937' }}>{truck?.current_location || '—'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>Transport Agents</h2>
          <p className="text-sm" style={{ color: '#6b7280' }}>Registered transport agents and fleet operators</p>
        </div>
      </div>

      <div className="relative mb-4">
        <Icon name="Search" size={16} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2" />
        <input 
          type="text" 
          placeholder="Search by name, mobile, or city..." 
          value={search} 
          onChange={e => setSearch(e?.target?.value)} 
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" 
          style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#1f2937' }}
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-3"></div>
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading agents…</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#fef3c7' }}>
              <p className="text-2xl font-bold" style={{ color: '#92400e' }}>{agents?.length}</p>
              <p className="text-xs" style={{ color: '#78350f' }}>Total Agents</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#d1fae5' }}>
              <p className="text-2xl font-bold" style={{ color: '#065f46' }}>
                {agents?.filter(a => a?.verification_status === 'verified')?.length}
              </p>
              <p className="text-xs" style={{ color: '#064e3b' }}>Verified</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#dbeafe' }}>
              <p className="text-2xl font-bold" style={{ color: '#1e40af' }}>
                {agents?.reduce((sum, a) => sum + (a?.truckCount || 0), 0)}
              </p>
              <p className="text-xs" style={{ color: '#1e3a8a' }}>Total Trucks</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#f3e8ff' }}>
              <p className="text-2xl font-bold" style={{ color: '#6b21a8' }}>
                {agents?.filter(a => a?.status === 'active')?.length}
              </p>
              <p className="text-xs" style={{ color: '#581c87' }}>Active</p>
            </div>
          </div>

          <div className="hidden sm:block rounded-xl border overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Agent Name</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Mobile</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>City, State</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>GST Number</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Trucks</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Status</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered?.map((agent, idx) => (
                  <tr 
                    key={agent?.id} 
                    style={{ 
                      backgroundColor: idx % 2 === 0 ? '#ffffff' : '#faf7f2', 
                      borderTop: '1px solid #f0e8de' 
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-bold" style={{ color: '#1f2937' }}>{agent?.agent_name}</div>
                      {agent?.email && (
                        <div className="text-xs" style={{ color: '#6b7280' }}>{agent?.email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: '#374151' }}>
                      {agent?.mobile}
                    </td>
                    <td className="px-4 py-3" style={{ color: '#374151' }}>
                      {agent?.city && agent?.state ? `${agent?.city}, ${agent?.state}` : '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: '#374151' }}>
                      {agent?.gst_number || <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewTrucks(agent?.id)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold hover:opacity-80"
                        style={{ 
                          backgroundColor: agent?.truckCount > 0 ? '#dbeafe' : '#f3f4f6',
                          color: agent?.truckCount > 0 ? '#1e40af' : '#6b7280'
                        }}
                      >
                        <Icon name="Truck" size={12} />
                        {agent?.truckCount}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        agent?.verification_status === 'verified'
                          ? 'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                      }`}>
                        <Icon 
                          name={agent?.verification_status === 'verified' ? 'CheckCircle' : 'Clock'} 
                          size={12} 
                        />
                        {agent?.verification_status === 'verified' ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewTrucks(agent?.id)}
                        className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:opacity-80"
                        style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-10" style={{ color: '#9ca3af' }}>
                      No transport agents found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {filtered?.map(agent => (
              <div 
                key={agent?.id} 
                className="rounded-xl border p-4" 
                style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="font-bold text-base mb-1" style={{ color: '#1f2937' }}>
                      {agent?.agent_name}
                    </div>
                    {agent?.email && (
                      <div className="text-xs mb-2" style={{ color: '#6b7280' }}>{agent?.email}</div>
                    )}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                      agent?.verification_status === 'verified'
                        ? 'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                    }`}>
                      <Icon 
                        name={agent?.verification_status === 'verified' ? 'CheckCircle' : 'Clock'} 
                        size={10} 
                      />
                      {agent?.verification_status === 'verified' ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleViewTrucks(agent?.id)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
                  >
                    <Icon name="Truck" size={13} />
                    {agent?.truckCount}
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div>
                    <span style={{ color: '#6b7280' }}>Mobile: </span>
                    <span className="font-semibold font-mono" style={{ color: '#1f2937' }}>{agent?.mobile}</span>
                  </div>
                  <div className="col-span-2">
                    <span style={{ color: '#6b7280' }}>Location: </span>
                    <span className="font-semibold" style={{ color: '#1f2937' }}>
                      {agent?.city && agent?.state ? `${agent?.city}, ${agent?.state}` : '—'}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span style={{ color: '#6b7280' }}>GST: </span>
                    <span className="font-semibold font-mono text-xs" style={{ color: '#1f2937' }}>
                      {agent?.gst_number || '—'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => handleViewTrucks(agent?.id)}
                  className="w-full py-2 rounded-lg text-xs font-semibold border"
                  style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TransportAgentsTab;
